import React, { FC } from "react";
import { Reservation } from "@/types/reservation";
import { FoldableContainer } from "@/components/pages/chats/component/booking-info-components/FoldableContainer";
import { useTranslation } from "react-i18next";
import { useChatContext } from "stream-chat-react";
import { useChatNotes, useDeleteChatNote } from "@/hooks/useChat.hooks";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface NotesBookingInfoProps {
  booking: Reservation;
}

export const NotesBookingInfo: FC<NotesBookingInfoProps> = ({ booking }) => {
  const { t } = useTranslation();
  const { channel } = useChatContext();
  const { data: chatNotes = [] } = useChatNotes(channel?.id);
  const { mutate: deleteChatNote, isPending: isDeletingNote } =
    useDeleteChatNote(channel?.id);
  const persistedNotes = booking.notes || [];
  const totalNotesCount = persistedNotes.length + chatNotes.length;

  const renderMarkdownInline = (text: string) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <>{children}</>,
      }}
    >
      {text.replace(/\n/g, "  \n")}
    </ReactMarkdown>
  );

  const renderFormattedText = (text?: string) => {
    if (!text) return null;

    const parts = text.split(/(\+\+[\s\S]+?\+\+)/g);

    return (
      <>
        {parts.map((part, index) => {
          const isUnderlinePart = part.startsWith("++") && part.endsWith("++");

          if (!isUnderlinePart) {
            return (
              <React.Fragment key={index}>
                {renderMarkdownInline(part)}
              </React.Fragment>
            );
          }

          const underlinedText = part.slice(2, -2);

          return (
            <span key={index} className="underline decoration-solid">
              {renderMarkdownInline(underlinedText)}
            </span>
          );
        })}
      </>
    );
  };

  if (totalNotesCount === 0) {
    return null;
  }

  return (
    <FoldableContainer
      titleNode={t("chats.notes")}
      extraNode={<span>({totalNotesCount})</span>}
    >
      <div className="mt-4 flex flex-col gap-3">
        {chatNotes.map((note) => (
          <div
            key={note.id}
            className="relative rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3"
          >
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-2.5 right-2.5 h-8 w-8 p-0 text-amber-700 hover:bg-amber-100 hover:text-amber-900"
              onClick={() => deleteChatNote(note.id)}
              disabled={isDeletingNote}
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only">{t("chats.delete")}</span>
            </Button>
            <div className="bg-transparent pr-10 text-base leading-7 text-slate-800 break-words whitespace-pre-wrap">
              {renderFormattedText(note.content)}
            </div>
          </div>
        ))}
        {persistedNotes.map((note, index) => (
          <div
            key={`${booking.id}-note-${index}`}
            className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"
          >
            {note}
          </div>
        ))}
      </div>
    </FoldableContainer>
  );
};
